import { useTheme } from '@mint-lib/styled';
import type {} from '@mint-lib/theme/src/extensions/circularProgress.js';
import CircularProgressM, {
  CircularProgressProps as CircularProgressPropsM,
} from '@mui/material/CircularProgress';

import Box from '../Box/Box.jsx';
import Typography from '../Typography/Typography.jsx';

export interface CircularProgressProps extends CircularProgressPropsM {
  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css-api) below for more details.
   */
  classes?: CircularProgressPropsM['classes'];
  /**
   */
  className?: string;
  /**
   * The variant to use.
   * Use indeterminate when there is no progress value.
   */
  variant?: CircularProgressPropsM['variant'];
  /**
   * The size of the circle.
   */
  size?: number | string;
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color?: CircularProgressPropsM['color'];
  /**
   */
  style?: CircularProgressPropsM['style'];
  /**
   * The thickness of the circle.
   */
  thickness?: number;
  /**
   * The value of the progress indicator for the determinate and static variants.
   * Value between 0 and 100.
   */
  value?: number;
  /**
   * If `true`, the shrink animation is disabled.
   * This only works if variant is `indeterminate`.
   */
  disableShrink?: boolean;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   * See the `sx` page for more details. https://mui.com/system/the-sx-prop/
   */
  sx?: CircularProgressPropsM['sx'];
  /**
   * If `true`, the CircularProgress will be with label
   */
  withLabel?: boolean;
  /**
   * Position of the label
   */
  labelPosition?: 'center' | 'right';
}

/**
 * @deprecated Use the new ProgressCircular component from @mint-lib/tw-components/patterns instead.
 */
const CircularProgress = ({
  withLabel,
  value,
  labelPosition = 'center',
  ...props
}: CircularProgressProps) => {
  const theme = useTheme();
  return (
    <Box
      className="circular-progress-container"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        '.MuiCircularProgress-colorPrimary': {
          color: (theme) => theme.palette.tertiary.light,
        },
        '.MuiCircularProgress-colorSecondary': {
          color: (theme) => theme.palette.secondary.main,
        },
        '.MuiCircularProgress-colorWarning': {
          color: (theme) => theme.palette.warning.main,
        },
        gap: '8px',
      }}
    >
      <CircularProgressM
        size="2.5rem"
        thickness={4.2}
        value={value}
        {...props}
        sx={{
          'svg circle': {
            stroke: props.color === 'ai' ? 'url(#circleGradient)' : '',
          },
          '.MuiCircularProgress-svg': {
            strokeLinecap: 'round',
          },
        }}
      />
      {props.color === 'ai' && (
        // Box to prevent svg from taking more place
        <Box
          position="absolute"
          width="100%"
          height="100%"
          data-testId="ai-loader-gradient"
        >
          <svg>
            <defs>
              <linearGradient
                id="circleGradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor={theme.palette.ai.lightGreen} />
                <stop offset="100%" stopColor={theme.palette.ai.lightCyan} />
              </linearGradient>
            </defs>
          </svg>
        </Box>
      )}
      {withLabel && value ? (
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: labelPosition !== 'center' ? 'relative' : 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '.MuiTypography-root': {
              ...(theme) => theme.typography.caption01,
            },
          }}
        >
          <Typography
            variant="caption01"
            component="div"
            color="text.secondary"
          >{`${Math.round(value)}%`}</Typography>
        </Box>
      ) : null}
    </Box>
  );
};
export default CircularProgress;
